// Analysis pages for the Phone Records (CDR) Analyzer: pattern of life and
// gaps, an incident window, contacts ranked by days, IMEI (handset) history,
// likely home/work cell sites, business-number flags and a case-summary PDF.
// Everything here is calculated in the browser from the parsed records.

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  AnalysisTheme, LIGHT, useT, useTimeFormat, Heading, Note, Explain, Chip, DataTable, GeneratedBadge,
  btnStyle, plural, useOnlineGate,
} from './ui';
import {
  AnalysisShell, PatternOfLifeView, IncidentView, ContactsView, HomeWorkCards, CaseSummaryView, summaryDoc, standardSections,
  downloadPdf,
} from './views';
import { contactRanking, nightWorkProfile, homeAndWork, valueHistory, valueChanges, phoneKey } from '../../utils/analysisKit';
import { lookupFoursquareBatch } from '../../utils/phoneIntel';

const TYPE_LABEL = { voice: 'Voice', sms: 'SMS', mms: 'MMS', data: 'Data', unknown: 'Other' };

// The page's records as analysis events. `toEpoch` turns a record's timestamp
// into a real instant using the page's source time zone.
export function phoneEvents(records, toEpoch, fmtNum) {
  const out = [];
  for (const r of records) {
    if (!r.timestamp) continue;
    const contact = r.type !== 'data' ? r.contactNumber || '' : '';
    out.push({
      t: toEpoch(r.timestamp), account: r.subNum || 'unknown', kind: r.type, source: TYPE_LABEL[r.type] || r.type,
      service: TYPE_LABEL[r.type] || r.type, activity: true, ips: [], handle: contact, direction: r.direction,
      places: r.cellId ? [r.cellId] : [], models: [], imei: (r.imei || '').trim(),
      detail: [
        r.direction && r.direction !== 'unknown' ? r.direction : '', TYPE_LABEL[r.type],
        contact && `${r.direction === 'incoming' ? 'from' : r.direction === 'outgoing' ? 'to' : 'with'} ${fmtNum(contact)}`,
        r.durationSeconds > 0 && `${r.durationSeconds}s`, r.cellId && `cell ${r.cellId}`, r.imei && `IMEI ${r.imei}`,
      ].filter(Boolean).join(' · '),
    });
  }
  return out.sort((a, b) => a.t - b.t);
}

const tenDigits = n => {
  const d = phoneKey(n);
  return d.length === 10 && !/^[01]/.test(d) ? d : '';
};

export default function PhoneAnalysis(props) {
  return (
    <AnalysisTheme.Provider value={LIGHT}>
      <PhoneAnalysisInner {...props} />
    </AnalysisTheme.Provider>
  );
}

function PhoneAnalysisInner({ records, responses, toEpoch, fmtNum, names, tz, tzLabel, scope, carrierLabel }) {
  const t = useT();
  const gate = useOnlineGate();
  const fmt = useTimeFormat(tz);
  const [page, setPage] = useState('pattern');
  const [biz, setBiz] = useState(null); // digits -> matches, once checked
  const [bizBusy, setBizBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const abortRef = useRef(null);

  const events = useMemo(() => phoneEvents(records, toEpoch, fmtNum), [records, toEpoch, fmtNum]);
  const accountLabel = useCallback(a => {
    const r = responses.find(x => (x.subNum || 'unknown') === a);
    return `${fmtNum(a) || a}${r ? ` (${carrierLabel(r.carrier)})` : ''}`;
  }, [responses, fmtNum, carrierLabel]);

  // The subscriber's own numbers, so calls to voicemail or between their own lines can be set apart.
  const own = useMemo(() => {
    const m = new Map();
    for (const r of responses) {
      const a = r.subNum || 'unknown';
      if (!m.has(a)) m.set(a, new Set());
      [r.subNum, ...(r.subInfo?.otherNumbers || []), r.subInfo?.mtn, r.subInfo?.userInfo?.msisdn].filter(Boolean).forEach(n => m.get(a).add(phoneKey(n)));
    }
    return m;
  }, [responses]);

  const contacts = useMemo(() => contactRanking(events, own, e => e.service, tz), [events, own, tz]);
  const cellProfile = useMemo(() => nightWorkProfile(events, tz, e => e.places), [events, tz]);
  const cellHomeWork = useMemo(() => homeAndWork(cellProfile, x => [x.key, x.key]), [cellProfile]);
  const imeiHistory = useMemo(() => valueHistory(events, e => e.imei), [events]);
  const imeiChanges = useMemo(() => valueChanges(events, e => e.imei), [events]);

  const checkBusinesses = async () => {
    if (!(await gate.ask('business-listings'))) return;
    const list = [...new Set(contacts.filter(c => !c.own).sort((a, b) => b.days - a.days || b.count - a.count)
      .map(c => tenDigits(c.handle)).filter(Boolean))].slice(0, 1000);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBizBusy(true);
    try {
      const res = await lookupFoursquareBatch(list, { signal: ctrl.signal });
      if (!ctrl.signal.aborted) setBiz(Object.fromEntries([...res.entries()].filter(([, m]) => m.length)));
    } finally {
      if (!ctrl.signal.aborted) setBizBusy(false);
    }
  };
  const bizOf = c => (biz ? biz[tenDigits(c.handle)] : null);
  const nameOf = c => {
    const n = names[c.handle];
    const b = bizOf(c);
    return [n, b && `${b[0].name} (business listing)`].filter(Boolean).join(' · ');
  };
  const bizRows = useMemo(() => (biz ? contacts.filter(c => biz[tenDigits(c.handle)]).map(c => ({ ...c, matches: biz[tenDigits(c.handle)] })) : []), [biz, contacts]);

  const downloadSummary = async () => {
    setPdfBusy(true);
    try {
      const sections = [];
      for (const r of responses.filter(x => records.some(rec => (rec.subNum || 'unknown') === (x.subNum || 'unknown')))) {
        const s = r.subInfo || {};
        sections.push({ heading: `Subscriber ${accountLabel(r.subNum || 'unknown')}`, kv: [
          ['Name', s.name], ['Account #', s.accountNumber], ['Address', [s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')],
          ['IMEIs on file', (s.imeis || []).join(', ')], ['Emails', (s.emails || []).join(', ')], ['Records in scope', r.count],
        ] });
      }
      const std = standardSections({ events, tz, fmt, contacts: contacts.map(c => ({ ...c, handle: `${fmtNum(c.handle)}${nameOf(c) ? ` (${nameOf(c)})` : ''}` })), contactLabel: 'Number' });
      sections.push(...std);
      if (cellProfile.length) {
        sections.push({ heading: 'Likely home and work cell sites', small: 'The cell ID used on the most different nights (22:00-06:00) and weekdays (09:00-17:00). Map it with the carrier\'s cell site list.',
          lines: [['Likely home cell', cellHomeWork.home], ['Likely work cell', cellHomeWork.work]].map(([k, g]) => (g ? `${k}: ${g.label} (${g.nights} nights, ${g.workdays} working days, ${g.count} records)` : `${k}: none seen on 3 or more days.`)) });
      }
      if (imeiHistory.length) {
        sections.push({ heading: 'Handsets (IMEIs) seen in the records', table: { headers: ['Account', 'IMEI', 'Records', 'First', 'Last'], rows: imeiHistory.map(x => [accountLabel(x.account), x.value, x.count, fmt(x.first), fmt(x.last)]), widths: ['*', 110, 45, 90, 90] } });
        if (imeiChanges.length) sections.push({ heading: 'Handset changes', table: { headers: ['Time', 'Account', 'From IMEI', 'To IMEI', 'Note'], rows: imeiChanges.slice(0, 40).map(x => [fmt(x.t), accountLabel(x.account), x.from, x.to, x.back ? 'switched back' : 'new handset']), widths: [90, '*', 95, 95, 55] } });
      }
      if (bizRows.length) {
        sections.push({ heading: 'Contacts that match a business listing', small: 'From the Foursquare Open Source Places listings used by the Commercial Phone Number Lookup tool. A match is not proof of who answered.',
          table: { headers: ['Number', 'Days', 'Business', 'Address'], rows: bizRows.slice(0, 30).map(c => [fmtNum(c.handle), c.days, c.matches[0].name, c.matches[0].address]), widths: [80, 30, '*', '*'] } });
      }
      await downloadPdf(summaryDoc({ title: 'Phone Records: Case Summary', scope, fmt, tzLabel, source: 'the carrier', sections }), `Phone records case summary ${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  };

  const items = [
    { id: 'pattern', label: 'Pattern of life' },
    { id: 'incident', label: 'Incident window' },
    { id: 'contacts', label: 'Contacts by day', badge: contacts.filter(c => !c.own).length },
    { id: 'imei', label: 'Handsets (IMEI)', badge: new Set(imeiHistory.map(x => x.value)).size },
    { id: 'cells', label: 'Cell sites', badge: cellProfile.length },
    { id: 'business', label: 'Business numbers', badge: biz ? bizRows.length : undefined },
    { id: 'summary', label: 'Case summary (PDF)' },
  ];

  return (
    <AnalysisShell items={items} active={page} onSelect={setPage}>
      {page === 'pattern' && (
        <PatternOfLifeView events={events} tz={tz} fmt={fmt} tzLabel={tzLabel}
          explain="Each square counts calls, texts and data sessions in that hour of that weekday, in the &quot;Show in&quot; time zone. Use the menu to look at one type: data sessions run in the background, so voice and texts show the person better." />
      )}
      {page === 'incident' && (
        <IncidentView events={events} tz={tz} fmt={fmt} tzLabel={tzLabel} accountLabel={accountLabel} homeWork={{}}
          handleLabel="Numbers in contact" placeLabel="Cell IDs" show={fmtNum}
          explain="Pick the time of the incident (in the &quot;Show in&quot; time zone) and how wide a window to look at." />
      )}
      {page === 'contacts' && (
        <>
          <Heading title="Contacts by day" sub="Every number the phone talked or texted with, ranked by how many different days." />
          <GeneratedBadge />
          <ContactsView contacts={contacts} fmt={fmt} tzLabel={tzLabel} accountLabel={accountLabel} label="Number" showDirection show={fmtNum}
            nameOf={nameOf} note="Names you assign on the main page, and business listings (once checked), show next to the number." />
        </>
      )}
      {page === 'imei' && (
        <>
          <Heading title="Handsets (IMEI)" sub="Which physical phones the line was used in, and when it moved between them." />
          <GeneratedBadge />
          <Explain>
            The IMEI identifies the handset; the phone number stays with the SIM. When the IMEI on the records changes, the SIM
            was moved into another phone. Switching back and forth can mean two phones sharing one SIM; a single change around
            the incident can mean a new or replacement phone. The first 8 digits (the TAC) identify the make and model.
            Only some carriers&rsquo; records include an IMEI on every call.
          </Explain>
          {!imeiHistory.length ? <Note>None of these records have an IMEI. Check the subscriber information for the IMEIs on file.</Note> : (
            <>
              {imeiChanges.length > 0
                ? <Note tone="warn">The handset changed {plural(imeiChanges.length, 'time')}{imeiChanges.some(c => c.back) ? ', including switches back to an earlier phone' : ''}.</Note>
                : <Note>One handset per line for the whole period.</Note>}
              <DataTable key={tz} rows={imeiHistory} exportName="IMEI history" initialSort={{ key: 'first', dir: 'asc' }}
                columns={[
                  { key: 'account', label: 'Line', text: r => accountLabel(r.account) },
                  { key: 'value', label: 'IMEI', mono: true, color: t.cyan },
                  { key: 'tac', label: 'TAC', mono: true, text: r => r.value.replace(/\D/g, '').slice(0, 8) },
                  { key: 'count', label: 'Records', sort: r => r.count },
                  { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                  { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                ]} />
              {imeiChanges.length > 0 && (
                <>
                  <Heading generated title="Handset changes" sub="Each point where the IMEI differs from the line's previous record." />
                  <DataTable key={'c' + tz} rows={imeiChanges} exportName="IMEI changes" initialSort={{ key: 't', dir: 'asc' }}
                    columns={[
                      { key: 't', label: `Changed by (${tzLabel})`, mono: true, text: r => fmt(r.t), sort: r => r.t },
                      { key: 'prevT', label: 'Old handset last seen', mono: true, text: r => fmt(r.prevT), sort: r => r.prevT },
                      { key: 'account', label: 'Line', text: r => accountLabel(r.account) },
                      { key: 'from', label: 'From IMEI', mono: true },
                      { key: 'to', label: 'To IMEI', mono: true, color: t.cyan },
                      { key: 'back', label: 'Note', generated: true, text: r => (r.back ? 'switched back to an earlier handset' : 'new handset'),
                        render: r => (r.back ? <Chip tone="warn">switched back</Chip> : <Chip>new handset</Chip>) },
                    ]} />
                </>
              )}
            </>
          )}
        </>
      )}
      {page === 'cells' && (
        <>
          <Heading title="Cell sites" sub="Which cell IDs the phone used, and when. The ones used most at night and during work hours are likely home and work." />
          <GeneratedBadge />
          <Explain>
            A cell ID is the tower (and usually the sector) that handled the call. A phone uses the towers near where it is, so
            the cell used on the most different nights is likely near where the person sleeps, and the one used most on weekdays
            09:00&ndash;17:00 likely near work. A tower can cover a wide area. To put these on a map, look the IDs up in the
            carrier&rsquo;s cell site list for the same period. This tool doesn&rsquo;t send them anywhere.
          </Explain>
          {!cellProfile.length ? <Note>None of these records have a cell ID. Verizon and AT&amp;T usually provide cell site data in a separate location or timing-advance return.</Note> : (
            <>
              <HomeWorkCards homeWork={cellHomeWork} fmt={fmt} profile={cellProfile} noun="cell site" groupNote="IDs" />
              <DataTable key={tz} rows={cellProfile} exportName="cell sites" initialSort={{ key: 'days', dir: 'desc' }}
                columns={[
                  { key: 'key', label: 'Cell ID', mono: true, color: t.cyan },
                  { key: 'count', label: 'Records', sort: r => r.count },
                  { key: 'days', label: 'Days', generated: true, title: 'Different days this cell was used', sort: r => r.days },
                  { key: 'nights', label: 'Nights', generated: true, title: 'Different nights (22:00–06:00) this cell was used', sort: r => r.nights },
                  { key: 'workdays', label: 'Workdays', generated: true, title: 'Different weekdays this cell was used 09:00–17:00', sort: r => r.workdays },
                  { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                  { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                  { key: 'sources', label: 'Used for', text: r => r.sources.join(', ') },
                  { key: 'accounts', label: 'Lines', text: r => r.accounts.map(accountLabel).join(', ') },
                ]} />
            </>
          )}
        </>
      )}
      {page === 'business' && (
        <>
          <Heading title="Business numbers" sub="Which contacts are listed businesses (pizza place, bank, Uber driver line), so you can set them aside or follow them up." />
          <GeneratedBadge />
          <Explain>
            The numbers are checked against the same business listings the Commercial Phone Number Lookup tool uses (Foursquare
            Open Source Places, about 15 million US businesses). The listings are data files on this site, downloaded by area
            code; the phone numbers are never sent anywhere. A match means a business listed that number, not that the business
            is who answered. Numbers that aren&rsquo;t businesses (most people) won&rsquo;t match.
          </Explain>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button style={btnStyle(t)} disabled={bizBusy} onClick={checkBusinesses}>
              {bizBusy ? 'Checking…' : biz ? 'Check again' : `Check ${plural(Math.min(1000, contacts.filter(c => !c.own && tenDigits(c.handle)).length), 'number')} against business listings`}
            </button>
            {biz && !bizBusy && <span style={{ fontSize: 12.5, color: t.muted }}>{plural(bizRows.length, 'contact')} matched a business listing.</span>}
          </div>
          {biz && (
            <DataTable rows={bizRows} exportName="business numbers" initialSort={{ key: 'days', dir: 'desc' }} emptyText="None of the contacts matched a business listing."
              columns={[
                { key: 'handle', label: 'Number', mono: true, color: t.cyan, text: r => fmtNum(r.handle) },
                { key: 'days', label: 'Days', sort: r => r.days },
                { key: 'count', label: 'Records', sort: r => r.count },
                { key: 'name', label: 'Business', generated: true, text: r => r.matches.map(m => m.name).join(' / ') },
                { key: 'type', label: 'Type', text: r => r.matches[0].type },
                { key: 'address', label: 'Address', text: r => r.matches[0].address },
                { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
              ]} />
          )}
        </>
      )}
      {page === 'summary' && (
        <CaseSummaryView scope={scope} busy={pdfBusy} onDownload={downloadSummary}
          explain="One document for the case file: the subscriber details, top contacts by days, the pattern-of-life heatmap and longest gaps, handsets and handset changes, likely home and work cell sites, and business matches (if you ran that check). It follows the Response and &quot;Show in&quot; choices at the top."
          counts={[
            ['Records', events.length.toLocaleString()],
            ['Contacts', contacts.filter(c => !c.own).length.toLocaleString()],
            ['Handsets (IMEIs)', new Set(imeiHistory.map(x => x.value)).size],
            ['Cell IDs', cellProfile.length],
            ['Business matches', biz ? bizRows.length : 'Not checked'],
          ]} />
      )}
    </AnalysisShell>
  );
}
